import React, { useReducer, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import classNames from 'classnames';
import { MdImportantDevices } from 'react-icons/md';

import { ActionSection, ModalWifiEnabled } from '@components';
import { useAppContext } from '@contexts/AppContext';
import useDevices from '@hooks/useDevices';
import useLayoutContext from '@hooks/useLayoutContext';
import PackageExpirationModal from '../PackageExpirationModal';
import { FCWithChildren } from '@types';
import { DeviceStatus } from '../../../../constants';
import SetUpPassword from '@pages/ManageDevices/components/SetUpPassword';
import Steps from '@pages/ManageDevices/components/Steps';

const position = 'mt-4 md:absolute md:left-[10px] md:right-[10px] lg:left-[150px] lg:right-auto';

const RegisteredDevices: FCWithChildren<{}> = () => {
  const history = useHistory();
  const { user } = useAppContext();
  const { wifiPasswordEnabled } = useLayoutContext();
  const addDeviceSectionRef = useRef<HTMLDivElement | null>(null);

  const { data } = useDevices();

  const [isWifiEnabledModalOpen, setIsWifiEnabledModalOpen] = useState(false);

  const devicesContainer = classNames('text-black', position);

  const addDeviceHandler = () =>
    history.push({ pathname: '/manage-devices', state: { isAddingDevice: true } });

  const goToWifiPasswordHandler = () => history.push('/manage-devices');

  const remainingAllowedDevices = () =>
    user?.maxDevices ? `${data?.length || 0} of ${user?.maxDevices}` : `${data?.length}`;

  const remainingAllowedSessions = () =>
    user?.maxSessions
      ? `${
          data?.filter((d) => [DeviceStatus.ACTIVE, DeviceStatus.ONLINE].includes(d.status))
            .length || 0
        } of ${user?.maxSessions}`
      : `${data?.length}`;

  const remainingAllowed = () =>
    user?.maxSessions ? remainingAllowedSessions() : remainingAllowedDevices();

  const userExpiredPackageWarning = () => user?.expiredPackageWarning;

  const expiredPackageWarning = () =>
    localStorage.getItem('confirmPackageExpiration') !== 'true' && userExpiredPackageWarning();

  const [isPackageExpirationModalOpen, togglePackageExpirationModal] = useReducer(
    (isOpen) => !isOpen,
    expiredPackageWarning(),
  );

  const accountButton = () => {
    togglePackageExpirationModal();
    localStorage.setItem('confirmPackageExpiration', 'true');
    history.push('/account');
  };

  const packagesButton = () => {
    togglePackageExpirationModal();
    localStorage.setItem('confirmPackageExpiration', 'true');
    history.push('/services');
  };

  return (
    <>
      <ModalWifiEnabled
        isOpen={isWifiEnabledModalOpen}
        preWiredKey={user?.preWiredKey}
        onClose={() => {
          setIsWifiEnabledModalOpen(false);
        }}
      />
      <PackageExpirationModal
        closeModal={togglePackageExpirationModal}
        accountButton={accountButton}
        packagesButton={packagesButton}
        isOpen={isPackageExpirationModalOpen}
      />

      {!userExpiredPackageWarning() && (
        <ActionSection className={devicesContainer}>
          <ActionSection.Header icon={<MdImportantDevices size={80} />}>
            {remainingAllowed()} {user?.maxSessions ? 'Active Devices' : 'Registered Devices'}
          </ActionSection.Header>
          <ActionSection.Body>
            <div className="flex w-full flex-wrap gap-6">
              <div className="flex-1">
                {wifiPasswordEnabled && <SetUpPassword />}
                <div className="ml-4 -mr-4">
                  <ActionSection.Link
                    onClick={() => history.push('/faq')}
                    className="w-full border-none"
                  >
                    Need help getting connected?
                  </ActionSection.Link>
                  <ActionSection.Link
                    onClick={() => {
                      setIsWifiEnabledModalOpen(true);
                    }}
                    className="w-full"
                  >
                    Quick Connection Guide
                  </ActionSection.Link>
                </div>
              </div>
              <div className="flex-1">
                <Steps ref={addDeviceSectionRef} />
              </div>
            </div>
          </ActionSection.Body>
        </ActionSection>
      )}
    </>
  );
};

export default RegisteredDevices;
